// Dependencies
import { useSnackbar } from "notistack";
import { FC, MouseEventHandler, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

// Components
import Categories from "../../../components/Category/Categories";
import CategoryInfo from "../../../components/Category/CategoryInfo";
import CategoryEdit from "../../../components/Category/CategoryEdit";
import AddIcon from "@mui/icons-material/Add";
import CategoryAdd from "../../../components/Category/CategoryAdd/CategoryAdd";
import LayoutDelete from "~/components/Layout/WalletDelete";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { If, When, Choose } from "jsx-control-statements";
import { HashLoading, IconButton } from "~/components/Common";
import { StyledInfoLabel, StyledModal } from "~/components/Layout/LayoutInfo";

// apis
import {
  addCategory,
  editCategory,
  deleteCategory,
  getCategoryInfo,
  getCategoryList,
} from "~/services/category";

// utils
import { getChangedValues } from "~/utils";

// Types
import { QUERY_KEY } from "~/enums";
import { TCategoryAddData, TQueryError } from "~/types";

// Styles
import {
  StyledAddBtn,
  StyledNotFound,
  StyledErrorMessage,
  StyledLayoutContent,
} from "~/components/Layout";
import {
  StyledList,
  StyledBackBtn,
  StyledCategoryList,
} from "./CategoryWrapper.styled";
import { StyledListWrapper } from "~/components/Transactions/TransactionList/TransactionList.styled";

interface CategoryWrapperProps {
  walletId: string;
  onClose: MouseEventHandler;
}

const CategoryWrapper: FC<CategoryWrapperProps> = ({ walletId, onClose }) => {
  // States
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [showAdd, setShowAdd] = useState<boolean>(false);
  const [categoryId, setCategoryId] = useState<string>("");

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  /**close Edit form when user choose another wallet */
  useEffect(() => {
    setShowEdit(false);
  }, [categoryId]);

  // Queries
  const categoryListQuery = useQuery({
    queryKey: QUERY_KEY.CATEGORY.CATEGORY_LIST,
    queryFn: () => getCategoryList(walletId),
    enabled: !!walletId,
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const categoryQuery = useQuery({
    queryKey: [QUERY_KEY.CATEGORY.CATEGORY_INFO, categoryId],
    queryFn: () => getCategoryInfo(categoryId),
    enabled: !!categoryId,
    onError(error: TQueryError) {},
  });

  const categoryData = categoryQuery.data;

  const categoryList = categoryListQuery.data || [];

  // Mutation
  const addCategoryMutation = useMutation({
    mutationFn: addCategory,
    onSuccess() {
      enqueueSnackbar("Add Category success!", { variant: "success" });
      queryClient.invalidateQueries(QUERY_KEY.CATEGORY.CATEGORY_LIST);
      handleCloseAddModal();
    },
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const editCategoryMutation = useMutation({
    mutationFn: editCategory,
    onSuccess() {
      enqueueSnackbar("Add Category success!", { variant: "success" });
      queryClient.invalidateQueries(QUERY_KEY.CATEGORY.CATEGORY_INFO);
      queryClient.invalidateQueries(QUERY_KEY.CATEGORY.CATEGORY_LIST);
      handleCancelEdit();
    },
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: deleteCategory,
    onSuccess() {
      enqueueSnackbar("Delete Category success!", { variant: "success" });
      queryClient.invalidateQueries(QUERY_KEY.CATEGORY.CATEGORY_LIST);
      setShowDelete(false);
      setShowDetail(false);
      setCategoryId("");
    },
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  // Methods
  /** Show Category Detail */
  const handleShowDetail = (walletId: string) => {
    setShowEdit(false);
    setShowDetail(true);
    setCategoryId(walletId);
  };
  /************************************ */

  /** Add Category */
  const handleOpenAddModal = () => {
    setShowAdd(true);
  };

  const handleCloseAddModal = () => {
    setShowAdd(false);
  };

  const handleSaveAdd = (data: TCategoryAddData) => {
    addCategoryMutation.mutate({
      ...data,
      walletId,
    });
  };
  /**************************************** */

  /** Edit Category */
  const handleShowEdit = () => {
    setShowEdit(true);
    setShowDetail(false);
  };

  const handleCancelEdit = () => {
    setShowEdit(false);
    setShowDetail(true);
  };

  const handleSaveEdit = (data: { name: string; iconId: string }) => {
    if (categoryId) {
      const editData = getChangedValues({ name: categoryData?.name }, data);
      editCategoryMutation.mutate({
        id: categoryId,
        ...editData,
      });
    }
  };
  /*************************************************** */

  /** Delete Wallet */
  const handleShowDelete = () => {
    setShowDelete(true);
  };

  const handleCloseDelete = () => {
    setShowDelete(false);
  };

  const handleDelete = () => {
    deleteCategoryMutation.mutate(categoryId);
  };
  /************************************************** */

  return (
    <StyledLayoutContent>
      <StyledListWrapper>
        <StyledInfoLabel>Category List</StyledInfoLabel>
        <StyledCategoryList>
          <StyledBackBtn>
            <IconButton onClick={onClose}>
              <ChevronLeftIcon />
            </IconButton>
          </StyledBackBtn>
          <StyledList>
            <Choose>
              <When condition={categoryListQuery.isLoading}>
                <HashLoading />
              </When>
              <When condition={categoryListQuery.isSuccess}>
                <If condition={!categoryList.length}>
                  <StyledNotFound>No category found!</StyledNotFound>
                </If>
                <If condition={categoryList.length}>
                  <Categories data={categoryList} onSelect={handleShowDetail} />
                </If>
              </When>
              <When condition={categoryListQuery.isError}>
                <StyledErrorMessage>
                  Error: {categoryListQuery.error?.message}
                </StyledErrorMessage>
              </When>
            </Choose>
          </StyledList>
          <StyledAddBtn
            onClick={handleOpenAddModal}
            variant={"outlined"}
            icon={<AddIcon />}
          >
            Create new category
          </StyledAddBtn>
        </StyledCategoryList>
      </StyledListWrapper>

      {/** For see detail */}
      <If condition={showDetail}>
        <CategoryInfo
          data={categoryData}
          error={categoryQuery?.error?.message}
          status={{ ...categoryQuery }}
          onEdit={handleShowEdit}
          onDelete={handleShowDelete}
        />
      </If>

      {/** For edit category */}
      <If condition={showEdit}>
        <CategoryEdit
          initialData={categoryData}
          onSubmit={handleSaveEdit}
          onCancel={handleCancelEdit}
        />
      </If>

      {/** For create category */}
      <StyledModal open={showAdd}>
        <div>
          <CategoryAdd
            onSubmit={handleSaveAdd}
            onCancel={handleCloseAddModal}
            parentList={categoryList}
          />
        </div>
      </StyledModal>

      {/** For delete category */}
      <StyledModal open={showDelete}>
        <div>
          <LayoutDelete
            title={"Are you sure to delete this category?"}
            name={categoryData?.name}
            onCancel={handleCloseDelete}
            onConfirm={handleDelete}
          />
        </div>
      </StyledModal>
    </StyledLayoutContent>
  );
};

export default CategoryWrapper;
